import { formatDate } from '@/common/application/utils/timeUtils';
import { CreateRaptorJobRequest } from '@/raptorjob/domain/job-create-type';
import { CreateMultiDayJobRequest, RaptorJobFormSubmitInput } from '@/raptorjob/domain/raptor-jobs-types';
import { Group, RaptorJobFormValues } from '@/raptorjob/presentation/RaptorJobValidationSchema';

const toHHmm = (time: Date | string | null | undefined) => {
  if (!time) return undefined;
  if (typeof time === 'string') return time;
  return new Date(time).toTimeString().slice(0, 8);
};

const mapEstimates = (estimates: Array<{ metricType: string; quantity: number | null; unit: string }> | undefined) =>
  (estimates || [])
    .filter((e) => e.metricType && e.quantity !== null && e.unit)
    .map((e) => ({ type: e.metricType, unit: e.unit, value: String(e.quantity) }));

const mapResources = (groups: Group[]) => {
  const resources = groups.map((g) => ({
    equipment: g.equipment?.equipmentId
      ? [
          {
            classId: g.equipment.equipmentClassId ?? 0,
            typeId: g.equipment.equipmentTypeId ?? 0,
            sizeId: g.equipment.equipmentSizeId ?? 0,
            unitId: g.equipment.equipmentId
          }
        ]
      : [],
    crew: g.employees.map((e) => ({ roleId: e.roleId, employeeId: e.employeeId })),
    items: [] as number[]
  }));
  return { resources };
};

export const useMapRaptorJobPayload = () => {
  const timeZone = (Intl?.DateTimeFormat?.().resolvedOptions().timeZone as string | undefined) || 'UTC';

  const mapSingleDayPayload = ({
    values,
    groups,
    idempotencyKey
  }: {
    values: RaptorJobFormValues;
    groups: Group[];
    idempotencyKey?: string;
  }): CreateRaptorJobRequest => {
    if (!values.startDate) {
      throw new Error('Start date is required');
    }

    const startDate = formatDate(values.startDate, 'yyyy-MM-dd')!;

    const jobTypeId = Number(values.jobTypeId);
    const customerId = Number(values.customerId);
    const jobSiteId = Number(values.jobSiteId);

    if (![jobTypeId, customerId, jobSiteId].every(Number.isFinite)) {
      throw new Error('Invalid job type, customer, or job site');
    }

    return {
      jobTypeId,
      customerId,
      jobSiteId,
      ...(values.name && values.name.length > 0 && { name: values.name }),
      startDate,
      description: values.jobDescription || '',
      internalNotes: values.jobNotes || '',
      crewNotes: values.notesForCrew || '',
      timeZone,
      dispatchTimes: {
        startTime: toHHmm(values.startTime),
        endTime: toHHmm(values.endTime),
        showUpTime: toHHmm(values.showUpTime),
        leaveTime: toHHmm(values.leaveByTime),
        onJobTime: toHHmm(values.onJobTime)
      },
      estimates: mapEstimates(values.estimates),
      ...mapResources(groups),
      options: { idempotencyKey: idempotencyKey || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` },
      ...(values.callerId && { callerId: values.callerId }),
      ...(values.calledAt && {
        calledAt: typeof values.calledAt === 'string' ? values.calledAt : new Date(values.calledAt as unknown as Date).toISOString()
      }),
      ...(values.confirmedBy && { confirmedBy: values.confirmedBy }),
      ...(values.confirmedAt && {
        confirmedAt:
          typeof values.confirmedAt === 'string' ? values.confirmedAt : new Date(values.confirmedAt as unknown as Date).toISOString()
      }),
      ...(values.cod && { cod: values.cod }),
      ...(values.poNumber && { poNumber: values.poNumber }),
      ...(values.salesAgentId && { salesAgentId: values.salesAgentId }),
      ...(values.salesRegionId && { salesRegionId: values.salesRegionId }),
      ...(values.vendorId && { vendorId: values.vendorId }),
      ...(values.flag1Id && { flag1Id: values.flag1Id }),
      ...(values.flag2Id && { flag2Id: values.flag2Id }),
      ...(values.flag3Id && { flag3Id: values.flag3Id }),
      ...(values.items && { items: values.items })
    };
  };

  const mapMultiDayPayload = (values: RaptorJobFormSubmitInput): CreateMultiDayJobRequest => {
    if (!values.startDate || !values.endDate) {
      throw new Error('Start date and end date are required');
    }

    const startDateStr = formatDate(values.startDate, 'yyyy-MM-dd')!;
    const endDateStr = formatDate(values.endDate, 'yyyy-MM-dd')!;

    const jobTypeId = Number(values.jobTypeId);
    const customerId = Number(values.customerId);
    const jobSiteId = Number(values.jobSiteId);

    if (![jobTypeId, customerId, jobSiteId].every(Number.isFinite)) {
      throw new Error('Invalid job type, customer, or job site');
    }

    return {
      jobTypeId,
      customerId,
      jobSiteId,
      name: values.jobDescription || 'New Job',
      description: values.jobDescription || '',
      internalNotes: values.jobNotes || undefined,
      crewNotes: values.notesForCrew || undefined,
      timeZone: undefined,
      recurrence: {
        startDate: startDateStr,
        endDate: endDateStr,
        estDurationDays: Math.max(0, values.estimatedDurationDays ?? 0)
      },
      dispatchTimes: {
        startTime: toHHmm(values.startTime),
        endTime: toHHmm(values.endTime),
        showUpTime: toHHmm(values.showUpTime),
        onJobTime: toHHmm(values.onJobTime)
      },
      estimates: mapEstimates(values.estimates),
      options: { idempotencyKey: undefined }
    };
  };

  return { mapSingleDayPayload, mapMultiDayPayload };
};
